/**
 * Airtable API utilities
 * Handles fetching tables and fields from Airtable bases
 */

// Airtable field types
export interface AirtableFieldChoice {
    id: string;
    name: string;
    color?: string;
}

export interface AirtableFieldOptions {
    choices?: AirtableFieldChoice[];
    linkedTableId?: string;
    isReversed?: boolean;
    prefersSingleRecordLink?: boolean;
    inverseLinkFieldId?: string;
    result?: {
        type: string;
        options?: {
            precision?: number;
            symbol?: string;
        };
    };
    precision?: number;
    symbol?: string;
    dateFormat?: {
        format: string;
        name: string;
    };
    timeFormat?: {
        format: string;
        name: string;
    };
    timeZone?: string;
}

export interface AirtableField {
    id: string;
    name: string;
    type: string;
    description?: string;
    options?: AirtableFieldOptions;
}

export interface AirtableTable {
    id: string;
    name: string;
    primaryFieldId: string;
    fields: AirtableField[];
    description?: string;
}

export interface AirtableTablesResponse {
    tables: AirtableTable[];
}

/**
 * Fetch tables metadata from Airtable base
 */
export async function fetchAirtableTables(
    baseId: string,
    apiToken: string
): Promise<AirtableTable[]> {
    const response = await fetch(
        `https://api.airtable.com/v0/meta/bases/${baseId}/tables`,
        {
            headers: {
                Authorization: `Bearer ${apiToken}`,
            },
        }
    );

    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Invalid API token. Please check your Airtable Personal Access Token.');
        }
        if (response.status === 404) {
            throw new Error('Base not found. Please check your Airtable Base ID.');
        }
        if (response.status === 403) {
            throw new Error('Access denied. Make sure your token has access to this base.');
        }
        throw new Error(`Failed to fetch tables: ${response.statusText}`);
    }

    const data: AirtableTablesResponse = await response.json();
    return data.tables;
}

/**
 * Map Airtable field type to our internal field type
 */
export function mapAirtableType(airtableType: string): {
    type: string;
    interface: string;
    special?: string[];
} {
    switch (airtableType) {
        case 'singleLineText':
        case 'email':
        case 'url':
        case 'phoneNumber':
            return { type: 'string', interface: 'input' };

        case 'multilineText':
            return { type: 'text', interface: 'input-multiline' };

        case 'richText':
            return { type: 'text', interface: 'input-rich-text-md' };

        case 'singleSelect':
            return { type: 'string', interface: 'select-dropdown' };

        case 'multipleSelects':
            return { type: 'json', interface: 'select-multiple-dropdown' };

        case 'checkbox':
            return { type: 'boolean', interface: 'boolean' };

        case 'number':
        case 'percent':
        case 'currency':
        case 'rating':
        case 'duration':
            return { type: 'float', interface: 'input' };

        case 'autoNumber':
        case 'count':
            return { type: 'integer', interface: 'input' };

        case 'date':
            return { type: 'date', interface: 'datetime' };

        case 'dateTime':
        case 'createdTime':
        case 'lastModifiedTime':
            return { type: 'timestamp', interface: 'datetime' };

        case 'multipleAttachments':
            return { type: 'json', interface: 'file', special: ['file'] };

        case 'singleCollaborator':
        case 'createdBy':
        case 'lastModifiedBy':
            return { type: 'uuid', interface: 'select-dropdown-m2o', special: ['m2o'] };

        case 'multipleCollaborators':
            return { type: 'json', interface: 'select-multiple-dropdown' };

        case 'multipleRecordLinks':
        case 'multipleLookupValues':
            return { type: 'json', interface: 'list-m2m', special: ['m2m'] };

        case 'formula':
        case 'rollup':
            return { type: 'string', interface: 'input' };

        case 'barcode':
            return { type: 'string', interface: 'input' };

        case 'button':
            return { type: 'alias', interface: 'presentation-links', special: ['alias'] };

        case 'externalSyncSource':
            return { type: 'string', interface: 'input' };

        case 'aiText':
            return { type: 'text', interface: 'input-multiline' };

        default:
            // Default to string input for unknown types
            return { type: 'string', interface: 'input' };
    }
}

/**
 * Convert a string to snake_case (lowercase with underscores)
 */
export function toSnakeCase(str: string): string {
    return str
        .toLowerCase()
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/[^a-z0-9_]/g, '') // Remove non-alphanumeric characters except underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Build choices array from Airtable select options
 * Keeps original values - no conversion to snake_case
 */
export function buildChoices(options?: AirtableFieldOptions): { text: string; value: string }[] | undefined {
    if (!options?.choices) return undefined;

    return options.choices.map(choice => ({
        text: choice.name,
        value: choice.name, // Keep original value, don't convert to snake_case
    }));
}

/**
 * Prepare field for import - maps Airtable field to our field creation payload
 */
export interface ImportFieldConfig {
    // Airtable source info
    airtableId: string;
    airtableName: string;
    airtableType: string;

    // Mapped/editable values
    field: string; // The key/column name
    type: string;
    interface: string;
    special?: string[];
    options?: Record<string, unknown>;
    description?: string;

    // Selection state
    selected: boolean;
}

export function prepareFieldForImport(airtableField: AirtableField): ImportFieldConfig {
    const mapping = mapAirtableType(airtableField.type);
    const choices = buildChoices(airtableField.options);

    const options: Record<string, unknown> = {};

    // Add choices if present
    if (choices && choices.length > 0) {
        options.choices = choices;
    }

    return {
        airtableId: airtableField.id,
        airtableName: airtableField.name,
        airtableType: airtableField.type,
        field: toSnakeCase(airtableField.name),
        type: mapping.type,
        interface: mapping.interface,
        special: mapping.special,
        options: Object.keys(options).length > 0 ? options : undefined,
        description: airtableField.description,
        selected: false,
    };
}

/**
 * Get a human-readable label for an Airtable type
 */
export function getAirtableTypeLabel(type: string): string {
    const labels: Record<string, string> = {
        singleLineText: 'Single Line Text',
        multilineText: 'Long Text',
        richText: 'Rich Text',
        singleSelect: 'Single Select',
        multipleSelects: 'Multiple Select',
        checkbox: 'Checkbox',
        number: 'Number',
        percent: 'Percent',
        currency: 'Currency',
        rating: 'Rating',
        duration: 'Duration',
        date: 'Date',
        dateTime: 'Date Time',
        createdTime: 'Created Time',
        lastModifiedTime: 'Last Modified Time',
        autoNumber: 'Auto Number',
        count: 'Count',
        email: 'Email',
        url: 'URL',
        phoneNumber: 'Phone',
        multipleAttachments: 'Attachments',
        singleCollaborator: 'User',
        multipleCollaborators: 'Users',
        createdBy: 'Created By',
        lastModifiedBy: 'Last Modified By',
        multipleRecordLinks: 'Linked Records',
        multipleLookupValues: 'Lookup',
        formula: 'Formula',
        rollup: 'Rollup',
        barcode: 'Barcode',
        button: 'Button',
        externalSyncSource: 'Sync Source',
        aiText: 'AI Text',
    };

    return labels[type] || type;
}

